import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode, $getNodeByKey } from 'lexical';
import * as React from 'react';
import { useRef, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Spinner } from '@fluentui/react-components';

export interface ImagePayload {
  altText: string;
  src: string;
  key?: NodeKey;
  width?: number;
  height?: number;
  s3Key?: string;
  isUploading?: boolean;
  uploadId?: string;
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    src: string;
    width?: number;
    height?: number;
    s3Key?: string;
    isUploading?: boolean;
    uploadId?: string;
  },
  SerializedLexicalNode
>;

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src } = domNode;
    const s3Key = domNode.getAttribute('s3-data-key') || undefined;
    const uploadId = domNode.getAttribute('data-upload-id') || undefined;

    // Parse width/height from inline styles first (preferred), fall back to HTML attributes for backward compatibility
    const width = domNode.style.width
      ? parseInt(domNode.style.width)
      : domNode.width || undefined;
    const height = domNode.style.height
      ? parseInt(domNode.style.height)
      : domNode.height || undefined;

    // Note: isUploading state is not preserved across HTML serialization
    // This is intentional - if we're importing from HTML, the upload is already done
    const node = $createImageNode({ altText, src, width, height, s3Key, uploadId });
    return { node };
  }
  return null;
}

type ImageComponentProps = {
  src: string;
  altText: string;
  width?: number;
  height?: number;
  nodeKey: NodeKey;
  isUploading?: boolean;
};

function ImageComponent({
  src,
  altText,
  width: initialWidth,
  height: initialHeight,
  nodeKey,
  isUploading = false,
}: ImageComponentProps): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const imageRef = useRef<HTMLImageElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState<number | undefined>(initialWidth);
  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    const image = imageRef.current;
    if (!image || initialWidth) {
      // If initialWidth is provided (from node), use it and skip auto-sizing
      return;
    }

    // Only auto-size if no width was provided (new image without dimensions)
    image.onload = () => {
      const naturalWidth = image.naturalWidth;
      const naturalHeight = image.naturalHeight;

      // Use natural dimensions - no scaling/constraints
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node && node instanceof ImageNode) {
          const writable = node.getWritable();
          writable.__width = naturalWidth;
          writable.__height = naturalHeight;
        }
      });
    };
  }, [editor, nodeKey, initialWidth]);

  const onResizeStart = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizing(true);

    const startX = event.clientX;
    const startWidth = currentWidth || imageRef.current?.clientWidth || 0;

    const onMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff);  // Only prevent collapse to 0
      setCurrentWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      // Read the ACTUAL current width from the DOM element, not from state
      // This avoids the stale closure issue
      if (imageRef.current) {
        const actualWidth = imageRef.current.clientWidth;
        const naturalRatio =
          imageRef.current.naturalHeight / imageRef.current.naturalWidth;
        const newHeight = actualWidth * naturalRatio;

        // Update the Lexical node with the actual resized dimensions
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if (node && node instanceof ImageNode) {
            const writable = node.getWritable();
            writable.__width = actualWidth;
            writable.__height = newHeight;
          }
        });
      }

      // Set isResizing to false AFTER updating the node
      // This prevents the sync logic from interfering (once removed in Step 1)
      setIsResizing(false);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      className="image-node-container"
      onMouseEnter={() => setIsSelected(true)}
      onMouseLeave={() => !isResizing && setIsSelected(false)}
      style={{ position: 'relative' }}
    >
      <img
        ref={imageRef}
        src={src}
        alt={altText}
        style={{
          width: currentWidth !== undefined ? `${currentWidth}px` :
                 (initialWidth ? `${initialWidth}px` : 'auto'),
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          cursor: isSelected ? 'pointer' : 'default',
          border: isSelected ? '2px solid #0066cc' : '2px solid transparent',
          borderRadius: '4px',
        }}
        draggable={false}
      />
      {isUploading && (
        <div className="image-upload-overlay">
          <Spinner size="tiny" label="Laddar upp bild..." labelPosition="below" />
        </div>
      )}
      {isSelected && !isUploading && (
        <div
          onMouseDown={onResizeStart}
          style={{
            position: 'absolute',
            right: '-5px',
            bottom: '-5px',
            width: '20px',
            height: '20px',
            backgroundColor: '#0066cc',
            border: '2px solid white',
            borderRadius: '50%',
            cursor: 'se-resize',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
          title="Drag to resize"
        />
      )}
    </div>
  );
}

export class ImageNode extends DecoratorNode<React.JSX.Element> {
  __src: string;
  __altText: string;
  __width?: number;
  __height?: number;
  __s3Key?: string;
  __isUploading?: boolean;
  __uploadId?: string;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__s3Key,
      node.__isUploading,
      node.__uploadId,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, src, width, height, s3Key, isUploading, uploadId } = serializedNode;
    const node = $createImageNode({
      altText,
      src,
      width,
      height,
      s3Key,
      isUploading,
      uploadId,
    });
    return node;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    width?: number,
    height?: number,
    s3Key?: string,
    isUploading?: boolean,
    uploadId?: string,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
    this.__s3Key = s3Key;
    this.__isUploading = isUploading;
    this.__uploadId = uploadId;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);

    // Export dimensions as inline styles (not HTML attributes) for better compatibility
    if (this.__width) {
      element.style.width = `${this.__width}px`;
    }
    if (this.__height) {
      element.style.height = `${this.__height}px`;
    }

    // Keep custom attributes for backend processing
    if (this.__s3Key) {
      element.setAttribute('s3-data-key', this.__s3Key);
    }
    if (this.__uploadId) {
      element.setAttribute('data-upload-id', this.__uploadId);
    }
    return { element };
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      height: this.__height,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width,
      s3Key: this.__s3Key,
      isUploading: this.__isUploading,
      uploadId: this.__uploadId,
    };
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  setWidthAndHeight(width: number, height: number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  updateAfterUpload(fetchUrl: string, s3Key: string): void {
    const writable = this.getWritable();
    writable.__src = fetchUrl;
    writable.__s3Key = s3Key;
    writable.__isUploading = false;
  }

  markUploadFailed(): void {
    const writable = this.getWritable();
    writable.__isUploading = false;
  }

  getS3Key(): string | undefined {
    return this.__s3Key;
  }

  isUploading(): boolean {
    return this.__isUploading || false;
  }

  getUploadId(): string | undefined {
    return this.__uploadId;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.JSX.Element {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        nodeKey={this.getKey()}
        isUploading={this.__isUploading}
      />
    );
  }
}

export function $createImageNode({
  altText,
  src,
  width,
  height,
  s3Key,
  isUploading,
  uploadId,
  key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(src, altText, width, height, s3Key, isUploading, uploadId, key)
  );
}

export function $isImageNode(
  node: LexicalNode | null | undefined
): node is ImageNode {
  return node instanceof ImageNode;
}
